import { InfoWindow } from '@vis.gl/react-google-maps';
import { useWaWorkplaceStore } from 'src/api/wa/store/WaWorkplaceStore';
import { useTranslation } from 'react-i18next';
import LinkButton from '../../button/LinkButton';
import { useNavigate } from 'react-router-dom';
import useWaWorkplaceService from 'src/api/wa/service/WaWorkplaceService';
import Button from '../../button/Button';
import { WaScreensMap } from 'src/ui/wards/router/WardsScreenMap';
import { WA_Screen } from 'src/ui/wards/router/WardsScreens';
import { wardsNavigate } from 'src/ui/wards/router/WardsScreenRegistration';
import { Workplace } from 'src/api/wa/store/models/workplace';
import SitesIcon from '../icon/SitesIcon';
import { getDetailStatusTranslationKey } from 'src/api/wa/store/models/workplace/OperationStatus';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import { Dispatch, SetStateAction } from 'react';
import { useNotify } from 'src/ui/common/alert/useNotify';
import { useWaUserStore } from 'src/api/wa/store/WaUserStore';
import { useNotifyWithParam } from 'src/ui/common/alert/useNotifyWithParam';

type MarkerProps = {
  site: Workplace;
  canOpenPopup: boolean;
  popupOpened?: () => void;
  popupClosed?: () => void;
  isPopupOpen?: boolean;
  selectedWorkerId?: string | null;
  setSelectedSite?: Dispatch<SetStateAction<Workplace | null>>;
};

function WorkplaceMarker({ site, canOpenPopup, popupClosed, isPopupOpen }: MarkerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userAgent = useUserAgent();
  const { setSelectedWorkplace } = useWaWorkplaceStore();
  const { createWorkplaceReservation } = useWaWorkplaceService();
  const { user } = useWaUserStore();
  const notify = useNotify();
  const notifyWithParam = useNotifyWithParam();

  if (!site) return null;

  const handleReserve = async () => {
    popupClosed?.();
    if (!user || !site) return;

    const reservationParams = [
      {
        engineerUserId: user.id,
        branchCode: site.branchCode,
        customerId: site.customerId,
      },
    ];

    try {
      const response = await createWorkplaceReservation(reservationParams);

      if (response) {
        notify('wards.sites.reservationSuccess');
      } else {
        notifyWithParam('wards.sites.reservationFailed', 'workplaces', site.customerId);
      }
    } catch (error) {
      console.log('error ', error);
    }
  };

  const handleSelected = async () => {
    setSelectedWorkplace(site);
  };

  const isReserved = site.details.reservations.length > 0;
  const icon = SitesIcon(isReserved, site.details.statusId, site.sectionCode);

  return (
    <>
      <div className="cursor-pointer">{icon}</div>

      {isPopupOpen && canOpenPopup && (
        <InfoWindow
          position={{ lat: site.location.latitude, lng: site.location.longitude }}
          onCloseClick={() => {
            popupClosed?.();
          }}
        >
          <div className="flex flex-col gap-2 p-1 text-sm text-black">
            <LinkButton
              title={site.customerId}
              onClick={() => {
                setSelectedWorkplace(site);
                wardsNavigate(navigate, WaScreensMap[WA_Screen.WorkplaceDetails].path);
              }}
            />
            <label className="m-0 text-center font-medium">
              {site.emergencyClass ? '★' : ''}
              {site.name}
            </label>
            <label className="m-0 text-center font-bold">
              {t(getDetailStatusTranslationKey(site.details.statusId))}
            </label>
            <label className="m-0 text-center font-bold">
              {site.details.comment ? t('wards.common.withComment') : t('wards.common.noComment')}
            </label>

            {!isReserved && !userAgent.isDesktop && (
              <Button
                className="rounded border border-blue-600 px-3 py-1 text-blue-600 transition hover:bg-blue-600 hover:text-white"
                title={t('wards.common.reserve')}
                onClick={handleReserve}
              />
            )}

            {userAgent.isDesktop && (
              <Button
                className="rounded border border-blue-600 px-3 py-1 text-blue-600 transition hover:bg-blue-600 hover:text-white"
                title={t('wards.common.select')}
                onClick={handleSelected}
              />
            )}
          </div>
        </InfoWindow>
      )}
    </>
  );
}
export default WorkplaceMarker;
